import type { LabResultsItem, TestTypeDefinitionItem } from '../../contexts/tableDefinitions';

/** A Date → "YYYY-MM-DD" using UTC parts (for date inputs / range boundaries). */
export const toLabDateYMD = (d: Date): string =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

// Reused instance — toLocaleDateString rebuilds the formatter on every call (~90x
// slower here). 'en-US' pins the format (en-GB gives "8 Sept 2025"); UTC keeps plain
// "YYYY-MM-DD" dates from rendering as the previous day in local time.
const LAB_DATE_LABEL_FMT = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
});

/** A Date → "Jun 8, 2025" (for axis/category labels). */
export const formatLabDateLabel = (d: Date): string => LAB_DATE_LABEL_FMT.format(d);

/** "YYYY-MM-DD" → epoch ms at the START of that UTC day (inclusive lower bound). */
export const labDateStartMs = (ymd: string): number => new Date(`${ymd}T00:00:00Z`).getTime();

/** "YYYY-MM-DD" → epoch ms at the END of that UTC day (inclusive upper bound). */
export const labDateEndMs = (ymd: string): number => new Date(`${ymd}T23:59:59Z`).getTime();

/**
 * Normalize any incoming test_date to a plain "YYYY-MM-DD" string.
 */
export const normalizeLabDate = (raw: string | null | undefined): string => {
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw; // already plain
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? String(raw) : toLabDateYMD(d);
};

// Friendly copy for the warning codes the lab OCR endpoint can emit.
export const WARNING_LABELS: Record<string, string> = {
    needs_manual_ocr: 'Scanned page — results may be imprecise.',
    handwritten_page_needs_manual_input: 'Handwritten page — verify every value.',
    no_fields_extracted: 'A page could not be read automatically — review it manually.',
    ocr_disabled: 'OCR is disabled — enter values manually.',
};

/**
 * The OCR endpoint returns warnings as objects ({ page, type, text_preview,
 * action }), but older/simpler responses may send plain strings. Normalize either
 * shape to a readable string — never let an object reach React as a child.
 */
export const warningText = (w: any): string => {
    if (w == null) return '';
    if (typeof w === 'string') return WARNING_LABELS[w] || w;
    if (typeof w === 'object') {
        const base = w.text_preview || WARNING_LABELS[w.type] || w.type || 'Warning';
        return w.page != null ? `${base} (page ${w.page})` : String(base);
    }
    return String(w);
};

/**
 * Lab ↔ Field pairs. `lab` is the official laboratory test_type;
 * `field` is the on-site witness value. Add new pairs here as they appear.
 */
export const FIELD_LAB_PAIRS: Record<string, { lab: string; field: string; label: string }> = {
    sediment: { lab: 'lab_sediment', field: 'field_sediment', label: 'Sediment (%)' },
    water: { lab: 'lab_water', field: 'field_water', label: 'Water (%)' },
};

export interface LabSampleGroup {
    key: string;
    test_date: string;
    sample_point: string | null;
    lease_id: number | null;
    lease_name: string | null;
    producer_name: string | null;
    market_id: number | null;
    market_name: string | null;
    station_name: string | null;
    /** test_type → numeric result */
    values: Record<string, number>;
    /** test_type → non-numeric result (copper_corrosion, specs, conclusion, etc.) */
    texts: Record<string, string>;
    /** test_type → original row id, for edit/delete from pivoted view */
    rowIds: Record<string, number>;
    /** Raw rows composing this group */
    rows: LabResultsItem[];
}

const keyOf = (r: LabResultsItem): string =>
    [r.test_date, r.sample_point || '', r.lease_id ?? '', r.market_id ?? ''].join('|');

/**
 * Pivots EAV lab_results rows into one group per logical sample
 * (same date + sample_point + lease/market). Each group exposes all test_type
 * values in a single object, enabling lab vs field comparison.
 */
export function pivotLabResults(rows: LabResultsItem[]): LabSampleGroup[] {
    const map = new Map<string, LabSampleGroup>();

    for (const r of rows) {
        const key = keyOf(r);
        let g = map.get(key);
        if (!g) {
            g = {
                key,
                test_date: r.test_date,
                sample_point: r.sample_point || null,
                lease_id: r.lease_id,
                lease_name: r.lease_name || null,
                producer_name: r.producer_name || null,
                market_id: r.market_id,
                market_name: r.market_name || null,
                station_name: r.station_name || null,
                values: {},
                texts: {},
                rowIds: {},
                rows: [],
            };
            map.set(key, g);
        }

        g.rows.push(r);
        g.rowIds[r.test_type] = r.id;

        const numeric = r.result != null ? Number(r.result) : NaN;
        if (!Number.isNaN(numeric)) {
            g.values[r.test_type] = numeric;
        } else if (r.result_text) {
            g.texts[r.test_type] = r.result_text;
        }
    }

    return Array.from(map.values());
}

/**
 * Given any test_type (lab or field side), returns its pair definition,
 * or null if the test_type has no field counterpart.
 */
export function getPairFor(testType: string) {
    for (const pair of Object.values(FIELD_LAB_PAIRS)) {
        if (pair.lab === testType || pair.field === testType) return pair;
    }
    return null;
}

/**
 * Identifier for a group in UI (graph series, table rows).
 * Falls back to sample_point when lease/market names are missing.
 */
export function describeGroupLocation(group: LabSampleGroup): string {
    if (group.lease_name) return group.lease_name;
    if (group.market_name) return group.market_name;
    if (group.station_name) return group.station_name;
    return group.sample_point || 'Unknown';
}

export type TestTypeDefMap = Map<string, TestTypeDefinitionItem>;

/** Index the catalog rows by their test_type slug. */
export const buildTestTypeDefMap = (defs: TestTypeDefinitionItem[] | undefined): TestTypeDefMap =>
    new Map((defs || []).map((d) => [d.test_type, d]));

/**
 * Best-effort label for a slug the catalog doesn't cover yet (still loading or a
 * brand-new test): de-snake + title-case, so a raw "lab_hydrogen_sulfide_vapor"
 * never reaches the UI as-is.
 */
const humanizeTestType = (slug: string): string =>
    slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Display label for a test_type: catalog display_name → humanized slug. */
export const labelForTestType = (testType: string, defs: TestTypeDefMap): string =>
    defs.get(testType)?.display_name || humanizeTestType(testType);

/** Unit for a test_type from the catalog ('' when unitless / not yet loaded). */
export const unitForTestType = (testType: string, defs: TestTypeDefMap): string =>
    defs.get(testType)?.unit || '';

export interface PivotedLabRow {
    /** Synthetic stable id (group key) — not a DB id. */
    id: string;
    test_date: string;
    /** Source kind for the group. */
    source: 'lease' | 'market' | 'unknown';
    lease_id: number | null;
    lease_name: string | null;
    producer_name: string | null;
    market_id: number | null;
    market_name: string | null;
    station_name: string | null;
    sample_point: string | null;
    lab_name: string | null;
    sampled_by: string | null;
    created_by: string | null;
    note: string | null;
    /** DB ids of every raw EAV row contained in this group. */
    _rowIds: number[];
    /** test_type → DB id (for edit/delete by-cell flows later). */
    _rowIdByTest: Record<string, number>;
    /** Allows arbitrary test_type columns. */
    [testType: string]: any;
}

const groupKey = (r: LabResultsItem): string => {
    if (r.lease_id) return `L|${r.test_date}|${r.lease_id}|${r.sample_point || ''}`;
    if (r.market_id) return `M|${r.test_date}|${r.market_id}|${r.sample_point || ''}`;
    return `X|${r.test_date}|${r.station_name || ''}|${r.sample_point || ''}`;
};

const pickFirst = (current: string | null, incoming: string | null | undefined) =>
    current || (incoming ? String(incoming) : null);

/**
 * Pivots EAV rows so each logical sample (Date + Lease/Producer or Date +
 * Station/Market) is a single row, with one column per test_type.
 *
 * Numeric tests fill the column with `result`; text tests (copper_corrosion,
 * specs, conclusion) fall back to `result_text`.
 */
export function buildPivotedRows(rows: LabResultsItem[]): PivotedLabRow[] {
    const map = new Map<string, PivotedLabRow>();

    for (const r of rows) {
        if (r.created_in_error) continue;
        const key = groupKey(r);
        let row = map.get(key);
        if (!row) {
            const source: PivotedLabRow['source'] = r.lease_id
                ? 'lease'
                : r.market_id
                    ? 'market'
                    : 'unknown';
            row = {
                id: key,
                // Normalize to a plain YYYY-MM-DD so the table never shows a time
                test_date: normalizeLabDate(r.test_date),
                source,
                lease_id: r.lease_id,
                lease_name: r.lease_name || null,
                producer_name: r.producer_name || null,
                market_id: r.market_id,
                market_name: r.market_name || null,
                station_name: r.station_name || null,
                sample_point: r.sample_point || null,
                lab_name: r.lab_name || null,
                sampled_by: r.sampled_by || null,
                created_by: r.created_by || null,
                note: null,
                _rowIds: [],
                _rowIdByTest: {},
            };
            map.set(key, row);
        }

        row.lease_name = pickFirst(row.lease_name, r.lease_name);
        row.producer_name = pickFirst(row.producer_name, r.producer_name);
        row.market_name = pickFirst(row.market_name, r.market_name);
        row.station_name = pickFirst(row.station_name, r.station_name);
        row.lab_name = pickFirst(row.lab_name, r.lab_name);
        row.sampled_by = pickFirst(row.sampled_by, r.sampled_by);
        row.created_by = pickFirst(row.created_by, r.created_by);
        row._rowIds.push(r.id);
        row._rowIdByTest[r.test_type] = r.id;

        const numeric = r.result != null ? Number(r.result) : NaN;
        if (!Number.isNaN(numeric)) {
            row[r.test_type] = numeric;
        } else if (r.result_text != null && r.result_text !== '') {
            row[r.test_type] = r.result_text;
        }

        if (r.note) {
            const existing = row.note ? row.note.split(' | ') : [];
            if (!existing.includes(r.note)) {
                existing.push(r.note);
                row.note = existing.join(' | ');
            }
        }
    }

    return Array.from(map.values()).sort((a, b) => {
        const ta = new Date(a.test_date || '').getTime();
        const tb = new Date(b.test_date || '').getTime();
        const aValid = !Number.isNaN(ta);
        const bValid = !Number.isNaN(tb);
        if (aValid && bValid && ta !== tb) return tb - ta; // newest first
        if (aValid !== bValid) return aValid ? -1 : 1;      // rows without a date last
        const la = a.lease_name || a.market_name || '';
        const lb = b.lease_name || b.market_name || '';
        return la.localeCompare(lb);
    });
}

/**
 * Orders a set of present test_types by the catalog's `display_order`;
 */
export function orderTestTypes(present: Set<string>, defs?: TestTypeDefMap): string[] {
    const orderOf = (tt: string): number => defs?.get(tt)?.display_order ?? Number.MAX_SAFE_INTEGER;
    return Array.from(present).sort((a, b) => orderOf(a) - orderOf(b) || a.localeCompare(b));
}

/**
 * Returns the test_types actually present across the given pivoted rows,
 * ordered by `orderTestTypes` (catalog `display_order` first when `defs` given).
 */
export function presentTestTypes(rows: PivotedLabRow[], defs?: TestTypeDefMap): string[] {
    const present = new Set<string>();
    for (const row of rows) {
        for (const tt of Object.keys(row._rowIdByTest)) present.add(tt);
    }
    return orderTestTypes(present, defs);
}